import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useConditionalEffect, useDelta } from 'react-delta';
import { VariableSizeList } from 'react-window';

import * as K from 'common/constants';
import { setMark } from 'common/PerformanceLogger';
import { getRowSizeCalculator } from 'components/search/logLineUtils';

import { windowHeightState } from 'components/common/Managers/LayoutStateManager';
import { useLogResultsState } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import MemoizedSearchLogLine from 'components/search/SearchLogsPage/SearchLogLine';
import { isScrollReadyState } from 'components/search/SearchLogsPage/SearchStateManager.state';

import 'components/search/SearchLogsPage/LogResults.scss';

type LogResultsProps = {
  logIds:          string[];
  heightOffset:    number;
};

/**
 * This component displays events as single-line rows within an "infinite" scrolling list that loads additional events
 * as the user scrolls.
 *
 * @prop logIds             list of continuationToken ids representing the events to display
 * @prop heightOffset       height in pixels reserved for the portion of the screen above this component
 */

function LogResults(
  {
    logIds = [],
    heightOffset,
  }: LogResultsProps,

): JSX.Element {

  const listRef = useRef<VariableSizeList>(null);
  const windowHeight = useRecoilValue(windowHeightState);
  const setIsScrollReady = useSetRecoilState(isScrollReadyState);

  const {
    itemData,
    onItemsRendered,
    scrollOffset,
  } = useLogResultsState();
  const { logEntryEdges, settings } = itemData;
  const itemCount = logEntryEdges?.length || 0;
  const offsetDelta = useDelta(scrollOffset);

  useLayoutEffect(() => {
    setMark('logResultRender', { itemCount, scrollOffset }, { debounce: 100 });
  });

  // reset all cached measurements when the log entry data or settings change
  useEffect(() => {
    if (listRef?.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [ logEntryEdges, settings ]);

  // When the offset changes, scroll to the appropriate point in the list and set isScrollReady to true so that the
  // onItemsRendered callback can resume threshold calculations.
  useConditionalEffect(() => {
    if (listRef?.current && (scrollOffset.cursor || scrollOffset.index !== undefined)) {
      const scrollToIndex = scrollOffset.index !== undefined ? scrollOffset.index : logIds.indexOf(scrollOffset.cursor as string);
      listRef.current.scrollToItem(scrollToIndex, scrollOffset.align);
      setIsScrollReady(true);
    }
  }, !!offsetDelta);

  const calculateRowSize = useCallback(getRowSizeCalculator(logEntryEdges, settings), [ logEntryEdges, settings ]);

  return (
    <VariableSizeList
      height={windowHeight - heightOffset}
      width="100%"
      itemCount={itemCount}
      itemData={itemData}
      itemSize={calculateRowSize}
      ref={listRef}
      onItemsRendered={onItemsRendered}
      overscanCount={K.LOG_RESULTS_OVERSCAN_COUNT}
    >
      {MemoizedSearchLogLine}
    </VariableSizeList>
  );
}

export default LogResults;
